import React, { useState, useMemo, useEffect } from 'react';
import { View, Text, Pressable, useWindowDimensions, Platform } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { MaterialIcons, FontAwesome5 } from '@expo/vector-icons';
import apis from '@api/api';
import DataChart from '@components/dataTables/DataChart';
import styles, { getModernCardStyle, getModernActionStyle, getModernTextColor } from '@stylesheet';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import DataSetChart from '@components/dataTables/Charts/DataSetChart';

interface UserStatsTrendsProps {
  startDate: Date;
  endDate: Date;
}

interface TrendData {
  date: string;
  totalFolders: number;
  totalContent: number;
  avgContentPerFolder: number;
  contentCreated: number;
  cumulativeFolders: number;
  cumulativeContent: number;
}

const UserStatsTrends: React.FC<UserStatsTrendsProps> = ({ startDate, endDate }) => {


  const theme = themeSelector();
  const { width } = useWindowDimensions();
  const [selectedMetric, setSelectedMetric] = useState<'folders' | 'content' | 'cumulative'>('folders');

  // Theme-aware styles
  const cardStyle = useMemo(() => getModernCardStyle(theme), [theme]);
  const textColor = useMemo(() => getModernTextColor(theme), [theme]);
  const secondaryTextColor = useMemo(() => getModernTextColor(theme, 'secondary'), [theme]);
  const mutedTextColor = useMemo(() => getModernTextColor(theme, 'muted'), [theme]);

  // Fetch user stats trends
  const { data: trendsData, isLoading, error, refetch } = useQuery({
    queryKey: ['userStatsTrends', startDate.toISOString(), endDate.toISOString()],
    queryFn: async () => {

      const response = await apis.getUserStatsTrends(
        startDate.toISOString(),
        endDate.toISOString(),
        'day'
      );

      return response.data || [];
    },
    enabled: !!startDate && !!endDate,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Process data for charts
  const chartData = useMemo(() => {
    try {

      if (!trendsData || trendsData.length === 0) {
        console.log('🔍 UserStatsTrends - No trends data available');
        return null;
      }

      const labels = trendsData.map((item: any) => {
        if (!item || !item.date) return 'Unknown Date';
        try {
          const date = new Date(item.date);
          return `${date.getMonth() + 1}/${date.getDate()}`;
        } catch (e) {
          return 'Invalid Date';
        }
      });

      let data: number[];
      let label: string;
      let color: string;

      switch (selectedMetric) {
        case 'folders':
          data = trendsData.map((item: any) => item?.totalFolders || 0);
          label = 'Folders Created';
          color = '#3b82f6';
          break;
        case 'content':
          data = trendsData.map((item: any) => item?.contentCreated || 0);
          label = 'Content Created';
          color = '#10b981';
          break;
        case 'cumulative':
          data = trendsData.map((item: any) => item?.cumulativeFolders || 0);
          label = 'Cumulative Folders';
          color = '#8b5cf6';
          break;
        default:
          data = trendsData.map((item: any) => item?.totalFolders || 0);
          label = 'Folders Created';
          color = '#3b82f6';
      }

      // Return DataChart format
      return {
        [selectedMetric]: {
          labels: labels.slice(-10), // Show last 10 data points for better readability
          data: data.slice(-10)
        }
      };
    } catch (error) {
      console.error('🔍 UserStatsTrends - Error processing chart data:', error);
      return null;
    }
  }, [trendsData, selectedMetric]);


  // Calculate summary stats - moved before conditional returns
  const summaryStats = useMemo(() => {
    if (!trendsData || trendsData.length === 0) return null;

    const totalFolders = trendsData.reduce((sum: number, item: any) => sum + (item?.totalFolders || 0), 0);
    const totalContent = trendsData.reduce((sum: number, item: any) => sum + (item?.contentCreated || 0), 0);
    const avgContentPerFolder = trendsData.reduce((sum: number, item: any) => sum + (item?.avgContentPerFolder || 0), 0) / trendsData.length;
    const lastItem = trendsData[trendsData.length - 1];

    return {
      totalFolders,
      totalContent,
      avgContentPerFolder: Math.round(avgContentPerFolder * 100) / 100,
      cumulativeFolders: lastItem?.cumulativeFolders || 0,
      cumulativeContent: lastItem?.cumulativeContent || 0
    };
  }, [trendsData]);

  if (isLoading) {
    return (
      <View style={[cardStyle, { marginHorizontal: 20, marginBottom: 16, padding: 20 }]}>
        <View style={styles.modernRow}>
          <View style={[styles.modernActionButton, getModernActionStyle(theme, 'grey')]}>
            <MaterialIcons name="trending-up" size={20} color={theme ? '#374151' : '#9ca3af'} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={[styles.modernFieldLabel, { color: mutedTextColor }]}>Data Activity Trends</Text>
            <Text style={[styles.modernStatsNumber, { color: textColor }]}>Loading...</Text>
          </View>
        </View>
      </View>
    );
  }

  if (error || !trendsData || trendsData.length === 0) {
    return (
      <View style={[cardStyle, { marginHorizontal: 20, marginBottom: 16, padding: 20 }]}>
        <View style={styles.modernRow}>
          <View style={[styles.modernActionButton, getModernActionStyle(theme, 'grey')]}>
            <MaterialIcons name="trending-up" size={20} color={theme ? '#374151' : '#9ca3af'} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={[styles.modernFieldLabel, { color: mutedTextColor }]}>Data Activity Trends</Text>
            <Text style={[styles.modernStatsNumber, { color: textColor }]}>
              {error ? 'Error loading data' : 'No data available'}
            </Text>
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={[cardStyle, { marginHorizontal: 20, marginBottom: 16, overflow: 'hidden' }]}>
      {/* Header */}
      <View style={[styles.modernRow, {
        marginBottom: 20
      }]}>
        <View style={{ flex: 1, flexDirection: 'column' }}>
          <Text style={[styles.modernFieldLabel, { color: mutedTextColor }]}>Data Activity Trends</Text>
          <Text style={[styles.modernStatsNumber, { textAlign: 'left', color: textColor, width: '100%' }]}>
            {summaryStats ? `${summaryStats.totalFolders} folders, ${summaryStats.totalContent} items` : 'No data'}
          </Text>
        </View>
        <Pressable
          style={[styles.modernIconButton, { maxWidth: 60 }, getModernActionStyle(theme, 'grey')]}
          onPress={() => refetch()}
        >
          <MaterialIcons name="refresh" size={16} color={theme ? '#374151' : '#9ca3af'} />
        </Pressable>
      </View>

      {/* Metric Selector */}
      <View style={{
        flexDirection: 'row',
        marginBottom: 16,
        gap: 8,
        flexWrap: 'wrap'
      }}>
        {[
          { key: 'folders', label: 'Folders', icon: 'folder', color: '#3b82f6' },
          { key: 'content', label: 'Content', icon: 'description', color: '#10b981' },
          { key: 'cumulative', label: 'Cumulative', icon: 'timeline', color: '#8b5cf6' }
        ].map((metric) => (
          <Pressable
            key={metric.key}
            onPress={() => setSelectedMetric(metric.key as any)}
            style={[
              styles.modernButton,
              getModernActionStyle(theme, selectedMetric === metric.key ? 'blue' : 'grey'),
              {
                minWidth: 100,
                paddingVertical: 8,
                paddingHorizontal: 12,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 4
              }
            ]}
          >
            <MaterialIcons
              name={metric.icon as any}
              size={16}
              color={selectedMetric === metric.key ? '#ffffff' : mutedTextColor}
            />
            <Text style={{
              color: selectedMetric === metric.key ? '#ffffff' : mutedTextColor,
              fontSize: 12,
              fontWeight: '600'
            }}>
              {metric.label}
            </Text>
          </Pressable>
        ))}
      </View>


      {/* Chart */}
      {chartData ? (
        <View style={{ marginBottom: 16, width: '100%' }}>
          {
            Platform.OS === 'web' ? (
              <DataSetChart
                title="Campaign Analytics"
                chartTitle="Performance Metrics"
                dotLabel="Folder Stats"
                minHeight={400}
                chartType="category"
                defaultDates={(() => {
                  // Create and validate dates
                  const startDate = new Date();
                  startDate.setDate(startDate.getDate() - 30); // 30 days ago
                  const endDate = new Date();

                  // Ensure dates are valid
                  const validStartDate = isNaN(startDate.getTime()) ? new Date(Date.now() - 30 * 24 * 60 * 60 * 1000) : startDate;
                  const validEndDate = isNaN(endDate.getTime()) ? new Date() : endDate;

                  return {
                    startDate: validStartDate,
                    endDate: validEndDate
                  };
                })()}
                dateCallback={() => { }}
                controls={<></>}
                containerStyle={{ width: '100%' }}
                chart={{
                  labels: chartData[selectedMetric].labels,
                  colors: [chartData[selectedMetric].color], // Use the color determined in chartData
                  options: {
                    theme: {
                      mode: theme ? 'light' : 'dark'
                    }
                  },
                  series: [
                    {
                      name: chartData[selectedMetric].label,
                      fill: 'solid',
                      data: chartData[selectedMetric].data,
                    },
                  ],
                }}
                subheader={"Performance metrics"}
              />
            ) :
              (
                <DataChart
                  reportData={chartData}
                  state={selectedMetric}
                  chartOnly={false}
                  hideLabels={false}
                  transparent={false}
                  style={{
                    width: '100%',
                    height: 300,
                    borderRadius: 16
                  }}
                />
              )
          }
        </View>
      ) : (
        // Fallback when no chart data
        <View style={{ alignItems: 'center', marginBottom: 16, padding: 20 }}>
          <Text style={{ color: mutedTextColor, fontSize: 14 }}>
            Chart data is being processed...
          </Text>
        </View>
      )}

      {/* Summary Stats */}
      {summaryStats && (
        <View style={{
          flexDirection: 'row',
          justifyContent: 'space-around',
          paddingTop: 16,
          borderTopWidth: 1,
          borderTopColor: theme ? '#e5e7eb' : '#374151'
        }}>
          {[
            {
              label: 'Total Folders',
              value: summaryStats.totalFolders,
              icon: 'folder',
              color: '#3b82f6'
            },
            {
              label: 'Total Content',
              value: summaryStats.totalContent,
              icon: 'description',
              color: '#10b981'
            },
            {
              label: 'Avg/Item',
              value: summaryStats.avgContentPerFolder,
              icon: 'analytics',
              color: '#8b5cf6'
            }
          ].map((stat, index) => (
            <View key={index} style={{ alignItems: 'center', flex: 1 }}>
              <MaterialIcons
                name={stat.icon as any}
                size={20}
                color={stat.color}
                style={{ marginBottom: 4 }}
              />
              <Text style={{
                color: textColor,
                fontSize: 18,
                fontWeight: '700',
                marginBottom: 2
              }}>
                {stat.value}
              </Text>
              <Text style={{
                color: mutedTextColor,
                fontSize: 11,
                textAlign: 'center'
              }}>
                {stat.label}
              </Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
};

export default UserStatsTrends;
